import type { LoaderFunctionArgs } from "react-router";
import { supabase } from "@shared/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

const ITEMS_PER_PAGE = 10;

type TestDataSetRow = Database["public"]["Tables"]["test_data_sets"]["Row"];
type TestDataEntry = Database["public"]["Tables"]["test_data_entries"]["Row"];
type TestRunRow = Database["public"]["Tables"]["test_runs"]["Row"];
type PgflowRunStatusRow =
	Database["public"]["Views"]["pgflow_run_statuses"]["Row"];
type ProjectRow = Database["public"]["Tables"]["projects"]["Row"];
type DatasetRunRow = Pick<
	TestRunRow,
	"id" | "project_id" | "created_at" | "status" | "type"
>;

export const datasetDetailLoader = async ({
	params,
	request,
}: LoaderFunctionArgs) => {
	const datasetId = params["datasetId"];
	if (!datasetId) {
		throw new Response("Dataset not found", { status: 404 });
	}

	const { data, error } = await supabase
		.from("test_data_sets")
		.select("*")
		.eq("id", datasetId)
		.single<TestDataSetRow>();

	if (error) {
		throw error;
	}
	if (!data) {
		throw new Response("Dataset not found", { status: 404 });
	}

	const url = new URL(request.url);
	const requestedPage = Number(url.searchParams.get("page") ?? "1");
	const page =
		Number.isNaN(requestedPage) || requestedPage < 1
			? 1
			: Math.floor(requestedPage);
	const from = (page - 1) * ITEMS_PER_PAGE;
	const to = from + ITEMS_PER_PAGE - 1;

	const [entriesRes, derivativesRes, testRunsRes, parentEntriesRes] =
		await Promise.all([
			supabase
				.from("test_data_entries")
				.select("*", { count: "exact" })
				.eq("test_data_set_id", datasetId)
				.order("created_at", { ascending: false })
				.range(from, to)
				.returns<TestDataEntry[]>(),
			supabase
				.from("test_data_sets")
				.select("*")
				.eq("parent_id", datasetId)
				.order("created_at", { ascending: false })
				.returns<TestDataSetRow[]>(),
			supabase
				.from("test_runs")
				.select("id, project_id, created_at, status, type")
				.eq("test_data_set_id", datasetId)
				.order("created_at", { ascending: false })
				.returns<
					Array<
						Pick<
							TestRunRow,
							"id" | "project_id" | "created_at" | "status" | "type"
						>
					>
				>(),
			data.parent_id
				? supabase
						.from("test_data_entries")
						.select("*")
						.eq("test_data_set_id", data.parent_id)
						.order("created_at", { ascending: false })
						.returns<TestDataEntry[]>()
				: Promise.resolve({ data: [], error: null }),
		]);

	if (entriesRes.error) throw entriesRes.error;
	if (derivativesRes.error) throw derivativesRes.error;
	if (testRunsRes.error) throw testRunsRes.error;
	if ("error" in parentEntriesRes && parentEntriesRes.error) {
		throw parentEntriesRes.error;
	}

	const entryCount = entriesRes.count ?? 0;
	const totalPages = Math.max(1, Math.ceil(entryCount / ITEMS_PER_PAGE));

	const runs = (testRunsRes.data ?? []) as DatasetRunRow[];
	const projectIds = [
		...new Set(
			runs
				.map((run) => run.project_id)
				.filter((id): id is string => Boolean(id)),
		),
	];
	let projectMap = new Map<string, { id: string; title: string | null }>();
	if (projectIds.length > 0) {
		const { data: projectsData, error: projectsError } = await supabase
			.from("projects")
			.select("id, title")
			.in("id", projectIds)
			.returns<Array<Pick<ProjectRow, "id" | "title">>>();

		if (projectsError) {
			throw projectsError;
		}

		projectMap = new Map(
			(projectsData ?? []).map((project) => [project.id, project]),
		);
	}

	let pgflowMap = new Map<string, PgflowRunStatusRow>();
	const runIds = runs
		.map((run) => run.id)
		.filter((id): id is string => Boolean(id));
	if (runIds.length > 0) {
		const { data: pgflowData, error: pgflowError } = await supabase
			.from("pgflow_run_statuses")
			.select("*")
			.in("test_run_id", runIds)
			.returns<PgflowRunStatusRow[]>();

		if (pgflowError) {
			throw pgflowError;
		}

		pgflowMap = new Map(
			(pgflowData ?? [])
				.filter((row): row is PgflowRunStatusRow & { test_run_id: string } =>
					Boolean(row.test_run_id),
				)
				.map((row) => [row.test_run_id!, row]),
		);
	}

	const testRuns = runs.map((run) => {
		const pgflow = pgflowMap.get(run.id);
		return {
			...run,
			projects: run.project_id ? projectMap.get(run.project_id) : undefined,
			pgflow_status: pgflow?.pgflow_status ?? null,
			pgflow_remaining_steps: pgflow?.remaining_steps ?? null,
			pgflow_latest_step_slug: pgflow?.latest_step_slug ?? null,
			pgflow_latest_step_status: pgflow?.latest_step_status ?? null,
			pgflow_last_error: pgflow?.last_error_message ?? null,
			pgflow_active_worker_id: pgflow?.active_worker_id ?? null,
		};
	});

	const hasDerivatives = (derivativesRes.data ?? []).length > 0;
	const hasTestRuns = runs.length > 0;
	let lockReason = "";
	let isLocked = false;
	if (hasDerivatives && hasTestRuns) {
		isLocked = true;
		lockReason =
			"This dataset has adversarial datasets and has been used in test runs";
	} else if (hasDerivatives) {
		isLocked = true;
		lockReason = "This dataset has adversarial datasets";
	} else if (hasTestRuns) {
		isLocked = true;
		lockReason = "This dataset has been used in test runs";
	}

	return {
		dataset: data,
		entries: entriesRes.data ?? [],
		entryCount,
		page,
		totalPages,
		derivatives: derivativesRes.data ?? [],
		parentEntries:
			"data" in parentEntriesRes
				? (parentEntriesRes.data as TestDataEntry[])
				: [],
		testRuns,
		lockState: { isLocked, lockReason },
	} as const;
};
